import { useEffect, useRef, useState } from "react";
import { t } from "@/lib/i18n";
import type { LogFilters } from "@/lib/types";

type SaveViewDialogProps = {
  open: boolean;
  filters: LogFilters;
  onClose: () => void;
  onSave: (name: string) => void;
};

export function SaveViewDialog({ open, filters, onClose, onSave }: SaveViewDialogProps) {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    onClose();
  };

  const summary = describeFilters(filters);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="w-[min(420px,90vw)] rounded-lg border border-border bg-card p-5 shadow-xl"
      >
        <h2 className="text-base font-medium">{t("Sicht speichern")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("Aktuelle Filter werden unter diesem Namen in der Seitenleiste verfügbar.")}
        </p>

        <label className="mt-4 block text-xs font-medium text-muted-foreground">{t("Name")}</label>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("z.B. Errors heute prod")}
          className="mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />

        <div className="mt-3 rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
          {summary || t("Keine aktiven Filter — diese Sicht zeigt alle Logs.")}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted"
          >
            {t("Abbrechen")}
          </button>
          <button
            type="submit"
            disabled={!name.trim()}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {t("Speichern")}
          </button>
        </div>
      </form>
    </div>
  );
}

const RANGE_TEXT: Record<string, string> = {
  "15m": t("letzte 15 Min"),
  "1h": t("letzte Stunde"),
  "6h": t("letzte 6 Std"),
  "24h": t("letzte 24 Std"),
  "7d": t("letzte 7 Tage"),
};

function describeFilters(f: LogFilters): string {
  const parts: string[] = [];
  if (f.range === "custom") {
    const from = f.from ? f.from.replace("T", " ") : "…";
    const to = f.to ? f.to.replace("T", " ") : t("jetzt");
    parts.push(t("Zeitraum {from} – {to}", { from, to }));
  } else if (f.range) {
    parts.push(t("Zeitraum {range}", { range: RANGE_TEXT[f.range] ?? f.range }));
  }
  if (f.level)
    parts.push(t("Level {op} {level}", { op: f.levelMode === "exact" ? "=" : "≥", level: f.level }));
  if (f.client) parts.push(`client = ${f.client}`);
  if (f.service) parts.push(`service = ${f.service}`);
  if (f.host) parts.push(`host = ${f.host}`);
  if (f.traceId) parts.push(`trace = ${f.traceId}`);
  if (f.search) parts.push(t('Suche "{q}"', { q: f.search }));
  if (f.limit && f.limit !== 100) parts.push(`limit ${f.limit}`);
  return parts.join(" · ");
}
